import { Button, Flex, Group, Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form/lib/types'
import PolicyCheckHOC from '@v2/commons/HOC/policyCheckHOC'
import ButtonFilter from '@v2/commons/components/FilterButton/FilterButton.component'
import SelectFilter from '@v2/commons/components/FilterSelect/FilterSelect.component'
import { TASK_PRIORITY_OPTIONS } from '@v2/domains/task/task.types'
import useGetUsersNoPaginationUseCase from '@v2/domains/user/hooks/useGetUsersNoPagination.useCase'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'
import { uniq } from 'lodash'
import { useEffect, useRef, useState } from 'react'

import {
  ESCALATION_STATUS_OPTIONS,
  IEscalation,
  IEscalationFilters,
  ISSUE_OPTIONS,
  NON_BENEFIT_OPTIONS,
} from '../../escalation.types'
import styles from './escalation-table-filters.module.scss'

interface EscalationFilterProps {
  escalationFiltersForm: UseFormReturnType<IEscalationFilters>
  isProgrammaticFormUpdateRef?: React.MutableRefObject<boolean>
  totalRecords: number
  filtersOpen: boolean
  setFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>
  escalations: IEscalation[]
}

const EscalationTableFilter = ({
  escalationFiltersForm,
  isProgrammaticFormUpdateRef,
  totalRecords,
  filtersOpen,
  escalations,
  setFiltersOpen,
}: EscalationFilterProps) => {
  const { assignees } = useGetUsersNoPaginationUseCase()
  const previousBenefits = useRef<string[]>([])
  const [benefitOptions, setBenefitOptions] = useState<
    string[] | { value: string; label: string }[]
  >([])

  useEffect(() => {
    const currentVlaues = escalationFiltersForm.getValues()
    if (currentVlaues.issue === 'nonBenefit') {
      setBenefitOptions(NON_BENEFIT_OPTIONS)
    } else if (!currentVlaues.issue) {
      setBenefitOptions([])
    } else {
      const benefits = uniq(
        escalations
          .map(({ benefit }) => benefit?.name)
          .filter((benefit) => !!benefit),
      ) as string[]

      setBenefitOptions(uniq([...benefits, ...previousBenefits.current]))
      previousBenefits.current = uniq([
        ...benefits,
        ...previousBenefits.current,
      ])
    }
  }, [escalations])

  const statusOptions = JSON.parse(
    JSON.stringify(ESCALATION_STATUS_OPTIONS),
  ).map((item: (typeof ESCALATION_STATUS_OPTIONS)[0]) => {
    item.disabled = false
    return item
  })

  const handleClearFilters = () => {
    escalationFiltersForm.reset()
  }
  escalationFiltersForm.watch('issue', ({ value, previousValue }) => {
    if (value === 'nonBenefit') {
      setBenefitOptions(NON_BENEFIT_OPTIONS)
    }
    if (value !== previousValue) {
      escalationFiltersForm.setFieldValue('benefit', '')
    }
  })

  const handleChangeValue = (
    path: keyof IEscalationFilters,
    value: IEscalationFilters[keyof IEscalationFilters],
  ) => {
    escalationFiltersForm.setFieldValue(path, value)
    if (isProgrammaticFormUpdateRef && isProgrammaticFormUpdateRef.current) {
      isProgrammaticFormUpdateRef.current = false
    }
  }

  return (
    <>
      <Flex justify="space-between" align="center" mb="30px">
        <ButtonFilter
          onClick={() => setFiltersOpen((prev) => !prev)}
          isOpen={filtersOpen}
        />
        <Text lh="26px" size="18px">
          Total Records: {totalRecords}
        </Text>
      </Flex>
      {filtersOpen && (
        <form style={{ marginBottom: '30px' }}>
          <Group
            preventGrowOverflow={false}
            grow={true}
            classNames={{ root: styles['group-root'] }}
          >
            <SelectFilter
              options={ISSUE_OPTIONS}
              placeholder="Issue"
              {...escalationFiltersForm.getInputProps('issue')}
              key={escalationFiltersForm.key('issue')}
              onChange={(value: IEscalationFilters['issue']) => {
                handleChangeValue('issue', value)
              }}
            />
            <SelectFilter
              options={benefitOptions}
              placeholder="Benefit / Service"
              searchable
              {...escalationFiltersForm.getInputProps('benefit')}
              key={escalationFiltersForm.key('benefit')}
              onChange={(value: IEscalationFilters['benefit']) => {
                handleChangeValue('benefit', value)
              }}
            />
            <PolicyCheckHOC
              policyName={WbfPolicies.is_get_all_escalations_enabled}
            >
              <SelectFilter
                options={assignees}
                searchable
                nothingFoundMessage="No users found"
                placeholder="Assigned To"
                {...escalationFiltersForm.getInputProps('assignedToUserId')}
                key={escalationFiltersForm.key('assignedToUserId')}
                onChange={(value: IEscalationFilters['assignedToUserId']) => {
                  handleChangeValue('assignedToUserId', value)
                }}
              />
            </PolicyCheckHOC>
            <SelectFilter
              options={statusOptions}
              placeholder="Status"
              {...escalationFiltersForm.getInputProps('formStatus')}
              key={escalationFiltersForm.key('formStatus')}
              onChange={(value: IEscalationFilters['formStatus']) => {
                handleChangeValue('formStatus', value)
              }}
            />
            <SelectFilter
              options={TASK_PRIORITY_OPTIONS}
              placeholder="priority"
              {...escalationFiltersForm.getInputProps('priority')}
              key={escalationFiltersForm.key('priority')}
              onChange={(value: IEscalationFilters['priority']) => {
                handleChangeValue('priority', value)
              }}
            />
            <Flex miw="50%" justify="flex-end">
              <Button
                variant="transparent"
                classNames={{ root: styles['clear-button-root'] }}
                onClick={handleClearFilters}
                type="button"
              >
                Clear All
              </Button>
            </Flex>
          </Group>
        </form>
      )}
    </>
  )
}

export default EscalationTableFilter
